import * as React from "react";
import { Page, Spinner, Tabs } from "@shopify/polaris";

import "./index.css";
import { useToast } from "@smartrr/vendor-portal/src/app/_sharedComponents/Toast/ToastProvider";
import { useIsSuperUserSelector } from "@smartrr/vendor-portal/src/app/_state/reducers/auth";
import { typedFrontendVendorApi } from "@smartrr/vendor-portal/src/utils/typedFrontendVendorApi";
import { LoyaltyUpsellCard } from "./LoyaltyUpsellCard";

interface Dashboard {
  id: string;
  label: string;
  url: string;
}

export function AdminAnalyticDashboardsRoute(): JSX.Element {
  const { addToast } = useToast();
  const [dashboards, setDashboards] = React.useState<Dashboard[]>([]);
  const [selectedTab, setSelectedTab] = React.useState(0);
  const [isLoading, setIsLoading] = React.useState(true);
  const [loyaltyEnabled, setLoyaltyEnabled] = React.useState(false);
  const isSuperUser = useIsSuperUserSelector();

  React.useEffect(() => {
    const fetchDashboards = async () => {
      try {
        const response = await typedFrontendVendorApi.getReq("/analytics/dashboards");

        if (response.type === "error") {
          addToast(`Error getting analytics`);
          setIsLoading(false);
          return;
        }
        const raw = response.body?.dashboards;
        const dashboardList = Array.isArray(raw) ? raw : [];
        setDashboards(dashboardList);
        setLoyaltyEnabled(response.body?.loyaltyEnabled ?? false);

        if (dashboardList.length === 0) {
          setIsLoading(false);
        }
      } catch (error) {
        if (isSuperUser) {
          console.log("Error:", error);
        }
        setIsLoading(false);
      }
    };
    void fetchDashboards();
  }, [addToast, isSuperUser]);

  const handleTabChange = React.useCallback((index: number) => {
    setSelectedTab(index);
  }, []);

  if (dashboards.length === 0) {
    if (!isLoading) {
      return <div />;
    }
    return (
      <div className="analytics-dashboards-page">
        <Page fullWidth>
          <div className="analytics-dashboards-container">
            <div className="analytics-dashboards-iframe-area">
              <div className="analytics-dashboards-loading">
                <Spinner size="large" />
              </div>
            </div>
          </div>
        </Page>
      </div>
    );
  }

  const tabs = [
    ...dashboards.map(d => ({ id: d.id, content: d.label })),
    ...(loyaltyEnabled ? [] : [{ id: "loyalty", content: "Loyalty" }]),
  ];

  return (
    <div className="analytics-dashboards-page">
      <Page fullWidth>
        <div className="analytics-dashboards-container">
          <Tabs tabs={tabs} selected={selectedTab} onSelect={handleTabChange} fitted />

          <div className="analytics-dashboards-iframe-area">
            {dashboards.map((dashboard, index) => (
              <div
                key={dashboard.id}
                style={{
                  display: index === selectedTab ? "block" : "none",
                  position: "absolute",
                  inset: 0,
                }}
              >
                <iframe
                  title={dashboard.label}
                  src={dashboard.url}
                  style={{ width: "100%", height: "100%", border: "none" }}
                  onLoad={() => setIsLoading(false)}
                />
              </div>
            ))}

            {isLoading ? (
              <div className="analytics-dashboards-loading">
                <Spinner size="large" />
              </div>
            ) : null}

            {!loyaltyEnabled && selectedTab === tabs.length - 1 && (
              <div className="analytics-dashboards-upsell">
                <div className="analytics-dashboards-upsell-inner">
                  <LoyaltyUpsellCard />
                </div>
              </div>
            )}
          </div>
        </div>
      </Page>
    </div>
  );
}
